import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getIncidents, uploadIncidents, analyzeIncident, createIncident, getOrganizations, getIncidentCategories, createPublicIncidentLink, getPublicIncidentLinks, revokePublicIncidentLink, updatePublicIncidentLink } from '../lib/api';
import { exportIncidentsReport } from '../lib/pdfExport';
import { useAuth } from '../context/AuthContext';
import { useAdminMode } from '../context/AdminModeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import PortfolioIncidents from '../components/PortfolioIncidents';
import IncidentReportForm from '../components/IncidentReportForm';
import IncidentWorkflowDashboard from '../components/IncidentWorkflowDashboard';
import { 
  Plus, 
  Upload, 
  Search, 
  AlertTriangle,
  Brain,
  Loader2,
  Calendar,
  MapPin,
  LayoutDashboard,
  LayoutGrid,
  List,
  Lightbulb,
  FileUp,
  Link as LinkIcon,
  TrendingUp,
  Clock,
  Zap,
  Target,
  CheckCircle,
  RefreshCw,
  ArrowUpRight,
  Info,
  AlertCircle,
  FileText,
  X,
  Check,
  Paperclip,
  Eye,
  Building2,
  Heart,
  Wrench,
  Clipboard,
  Users,
  Cloud,
  Shield,
  Download,
  Pencil,
} from 'lucide-react';

// Category icons for display
const CATEGORY_ICONS = {
  abuse_misconduct: AlertTriangle,
  injury_medical: Heart,
  facility_premises: Building2,
  equipment: Wrench,
  operational: Clipboard,
  behavior_conduct: Users,
  environmental: Cloud,
  other_safety: Shield,
};

const SEVERITY_OPTIONS = ['low', 'medium', 'high', 'critical'];
const CATEGORY_OPTIONS = ['Safety', 'Security', 'Injury', 'Property Damage', 'Policy Violation', 'Behavioral', 'Facility', 'Other'];
const STATUS_OPTIONS = ['open', 'in-progress', 'resolved', 'imported'];

// AI Insights Component
const AIInsightsTab = ({ incidents, organizations }) => {
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('30');
  const [activeSubTab, setActiveSubTab] = useState('recommendations');
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [completedTasks, setCompletedTasks] = useState(() => {
    const saved = localStorage.getItem('riskhero-completed-tasks');
    return saved ? JSON.parse(saved) : {};
  });

  // Save completed tasks to localStorage
  const saveCompletedTask = (taskId, proof) => {
    const updated = {
      ...completedTasks,
      [taskId]: {
        completedAt: new Date().toISOString(),
        proof: proof
      }
    };
    setCompletedTasks(updated);
    localStorage.setItem('riskhero-completed-tasks', JSON.stringify(updated));
  };

  // Generate intelligent insights from incidents
  const generateInsights = () => {
    const insights = [];
    
    // Analyze severity patterns
    const criticalIncidents = incidents.filter(i => i.severity === 'critical' || i.severity === 'high');
    const recentIncidents = incidents.filter(i => {
      const date = new Date(i.incident_date);
      const daysAgo = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo <= parseInt(timeRange);
    });

    // Category analysis
    const categoryCount = {};
    const locationCount = {};
    recentIncidents.forEach(inc => {
      categoryCount[inc.category] = (categoryCount[inc.category] || 0) + 1;
      if (inc.location) {
        locationCount[inc.location] = (locationCount[inc.location] || 0) + 1;
      }
    });

    // Find top category
    const topCategory = Object.entries(categoryCount).sort((a, b) => b[1] - a[1])[0];
    const topLocation = Object.entries(locationCount).sort((a, b) => b[1] - a[1])[0];

    // Generate critical insight if there are injury incidents
    const injuryIncidents = recentIncidents.filter(i => i.category === 'Injury');
    if (injuryIncidents.length > 0) {
      insights.push({
        id: 'injury-spike',
        severity: 'critical',
        title: 'Injury Incident Spike',
        description: `${injuryIncidents.length} injury incidents reported in the past ${timeRange} days - requires immediate attention`,
        location: topLocation ? topLocation[0] : 'Multiple Locations',
        recommendation: 'Review safety protocols immediately',
        impact: 'high',
        details: {
          affectedIncidents: injuryIncidents,
          rootCauses: ['Inadequate supervision', 'Equipment issues', 'Environmental hazards'],
          suggestedActions: [
            'Conduct immediate safety audit of all facilities',
            'Review and update injury prevention protocols',
            'Increase supervision during high-risk activities',
            'Schedule staff safety refresher training'
          ],
          timeline: 'Immediate action required within 48 hours',
          resources: ['Safety Audit Checklist', 'Injury Prevention Guide', 'Staff Training Materials']
        }
      });
    }

    // Location-based insight
    if (topLocation && topLocation[1] > 1) {
      insights.push({
        id: 'location-hotspot',
        severity: 'warning',
        title: 'Location Hotspot Detected',
        description: `${topLocation[1]} incidents reported at ${topLocation[0]} in the past ${timeRange} days`,
        location: topLocation[0],
        recommendation: 'Increase supervision and conduct safety audit',
        impact: 'medium',
        details: {
          affectedIncidents: recentIncidents.filter(i => i.location === topLocation[0]),
          rootCauses: ['High traffic area', 'Inadequate lighting', 'Equipment placement'],
          suggestedActions: [
            'Conduct thorough inspection of the location',
            'Review camera footage if available',
            'Increase staff presence during peak hours',
            'Consider physical modifications to improve safety'
          ],
          timeline: 'Complete assessment within 1 week',
          resources: ['Location Safety Assessment Form', 'Incident Heat Map']
        }
      });
    }

    // Time pattern insight
    if (recentIncidents.length > 2) {
      insights.push({
        id: 'time-pattern',
        severity: 'info',
        title: 'Time Pattern Alert',
        description: `${recentIncidents.length} incidents in the analysis period - review scheduling and staffing`,
        location: 'All Locations',
        recommendation: 'Adjust staffing levels during peak times',
        impact: 'medium',
        details: {
          affectedIncidents: recentIncidents,
          rootCauses: ['Understaffing during busy periods', 'Scheduling gaps', 'Fatigue-related issues'],
          suggestedActions: [
            'Analyze incident timing patterns',
            'Adjust staff schedules to cover high-risk periods',
            'Implement break schedules to reduce fatigue',
            'Consider additional temporary staffing'
          ],
          timeline: 'Implement changes within 2 weeks',
          resources: ['Staffing Analysis Template', 'Scheduling Best Practices Guide']
        }
      });
    }

    // Category insight
    if (topCategory && topCategory[1] > 1) {
      insights.push({
        id: 'category-trend',
        severity: topCategory[0] === 'Injury' || topCategory[0] === 'Security' ? 'warning' : 'info',
        title: `${topCategory[0]} Trend`,
        description: `${topCategory[0]} incidents represent ${Math.round((topCategory[1] / recentIncidents.length) * 100)}% of all incidents`,
        location: 'Organization-wide',
        recommendation: `Focus training on ${topCategory[0].toLowerCase()} prevention`,
        impact: topCategory[1] > 3 ? 'high' : 'medium',
        details: {
          affectedIncidents: recentIncidents.filter(i => i.category === topCategory[0]),
          rootCauses: ['Knowledge gaps', 'Policy compliance issues', 'Environmental factors'],
          suggestedActions: [
            `Develop targeted ${topCategory[0].toLowerCase()} prevention training`,
            'Review and update relevant policies',
            'Implement regular compliance checks',
            'Create awareness campaign for staff and participants'
          ],
          timeline: 'Develop program within 30 days',
          resources: ['Training Development Guide', 'Policy Templates', 'Awareness Materials']
        }
      });
    }

    return insights.length > 0 ? insights : [
      {
        id: 'no-issues',
        severity: 'info',
        title: 'No Critical Patterns Detected',
        description: 'Current incident levels are within normal parameters',
        location: 'All Locations',
        recommendation: 'Continue monitoring and maintain current protocols',
        impact: 'low',
        details: {
          affectedIncidents: [],
          rootCauses: [],
          suggestedActions: ['Continue regular monitoring', 'Maintain current safety protocols'],
          timeline: 'Ongoing',
          resources: ['Safety Monitoring Checklist']
        }
      }
    ];
  };

  // Generate action items
  const generateActionItems = () => {
    const actions = [];
    const behavioralIncidents = incidents.filter(i => i.category === 'Behavioral' || i.category === 'Policy Violation');
    const facilityIncidents = incidents.filter(i => i.category === 'Facility' || i.category === 'Property Damage');
    const injuryIncidents = incidents.filter(i => i.category === 'Injury');

    if (behavioralIncidents.length > 0) {
      actions.push({
        id: 'safesport-training',
        title: 'Mandatory SafeSport Refresher',
        priority: 'high',
        category: 'Training',
        description: 'All volunteer coaches should complete SafeSport refresher training',
        reason: `Based on ${behavioralIncidents.length} behavioral incidents involving staff`,
        timeline: 'Complete within 30 days',
        effort: 'Medium',
        impact: 'High',
        requirements: [
          'Certificate of completion from approved SafeSport provider',
          'Signed acknowledgment form',
          'Attendance roster for group training sessions'
        ]
      });
    }

    if (facilityIncidents.length > 0) {
      actions.push({
        id: 'facility-checks',
        title: 'Daily Facility Safety Checks',
        priority: 'high',
        category: 'Facility',
        description: 'Implement mandatory facility safety checklist before activities',
        reason: `${facilityIncidents.length} facility-related incidents reported`,
        timeline: 'Implement immediately',
        effort: 'Low',
        impact: 'High',
        requirements: [
          'Completed daily checklist forms',
          'Photos of any issues found and corrected',
          'Maintenance work orders if applicable'
        ]
      });
    }

    if (injuryIncidents.length > 1) {
      actions.push({
        id: 'first-aid-training',
        title: 'First Aid & CPR Certification',
        priority: 'high',
        category: 'Training',
        description: 'Ensure all frontline staff have current First Aid/CPR certification',
        reason: `Multiple injury incidents (${injuryIncidents.length}) require improved emergency response capability`,
        timeline: 'Complete within 60 days',
        effort: 'Medium',
        impact: 'High',
        requirements: [
          'Copy of certification card',
          'Training provider documentation',
          'Staff acknowledgment of emergency procedures'
        ]
      });
    }

    // Default action if no specific patterns
    if (actions.length === 0) {
      actions.push({
        id: 'regular-review',
        title: 'Quarterly Safety Review',
        priority: 'medium',
        category: 'Compliance',
        description: 'Schedule quarterly safety protocol review meeting',
        reason: 'Best practice for maintaining safety standards',
        timeline: 'Schedule within 2 weeks',
        effort: 'Low',
        impact: 'Medium',
        requirements: [
          'Meeting minutes',
          'Action items from review',
          'Sign-off sheet from attendees'
        ]
      });
    }

    return actions;
  };

  const insights = generateInsights();
  const actionItems = generateActionItems();

  const getSeverityConfig = (severity) => {
    const configs = {
      critical: { 
        bg: 'bg-red-50 dark:bg-red-950/30', 
        border: 'border-red-200 dark:border-red-900',
        badge: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400',
        icon: AlertTriangle
      },
      warning: { 
        bg: 'bg-amber-50 dark:bg-amber-950/30', 
        border: 'border-amber-200 dark:border-amber-900',
        badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
        icon: AlertCircle
      },
      info: { 
        bg: 'bg-blue-50 dark:bg-blue-950/30', 
        border: 'border-blue-200 dark:border-blue-900',
        badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
        icon: Info
      }
    };
    return configs[severity] || configs.info;
  };

  const handleUploadProof = () => {
    if (!uploadFile || !selectedAction) {
      toast.error('Please select a file to upload');
      return;
    }

    // In a real app, this would upload to a server
    // For now, we'll simulate by storing metadata locally
    const proof = {
      fileName: uploadFile.name,
      fileSize: uploadFile.size,
      uploadedAt: new Date().toISOString(),
      notes: uploadNotes
    };

    saveCompletedTask(selectedAction.id, proof);
    toast.success('Proof uploaded successfully! Task marked as complete.');
    setSelectedAction(null);
    setUploadFile(null);
    setUploadNotes('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
              Intelligent Insights
            </h2>
            <p className="text-sm text-muted-foreground">
              AI-powered analysis and recommendations based on incident patterns
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => { setLoading(true); setTimeout(() => setLoading(false), 1000); }} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Analysis
          </Button>
        </div>
      </div>

      {/* Insight Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {insights.map((insight) => {
          const config = getSeverityConfig(insight.severity);
          const IconComponent = config.icon;
          
          return (
            <Card key={insight.id} className={`${config.bg} ${config.border} border`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <IconComponent className="w-4 h-4 text-muted-foreground" />
                    <Badge className={`${config.badge} text-xs uppercase font-medium`}>
                      {insight.severity}
                    </Badge>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
                </div>
                
                <h3 className="font-semibold text-lg mb-2">{insight.title}</h3>
                <p className="text-sm text-muted-foreground mb-3">{insight.description}</p>
                
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-4">
                  <MapPin className="w-3 h-3" />
                  {insight.location}
                </div>
                
                <div className="bg-background/50 rounded-md p-3 mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-muted-foreground">Recommendation:</span>
                    <Badge variant="outline" className="text-xs">
                      {insight.impact} impact
                    </Badge>
                  </div>
                  <p className="text-sm">{insight.recommendation}</p>
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setSelectedInsight(insight)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Insight Details Dialog */}
      <Dialog open={!!selectedInsight} onOpenChange={() => setSelectedInsight(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              {selectedInsight && (
                <Badge className={getSeverityConfig(selectedInsight.severity).badge}>
                  {selectedInsight.severity}
                </Badge>
              )}
            </div>
            <DialogTitle className="text-xl">{selectedInsight?.title}</DialogTitle>
            <DialogDescription>{selectedInsight?.description}</DialogDescription>
          </DialogHeader>
          
          {selectedInsight?.details && (
            <div className="space-y-6 mt-4">
              {/* Root Causes */}
              {selectedInsight.details.rootCauses.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    Potential Root Causes
                  </h4>
                  <ul className="space-y-1">
                    {selectedInsight.details.rootCauses.map((cause, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                        {cause}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Suggested Actions */}
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Suggested Actions
                </h4>
                <ul className="space-y-2">
                  {selectedInsight.details.suggestedActions.map((action, i) => (
                    <li key={i} className="text-sm flex items-start gap-2 p-2 rounded bg-secondary/30">
                      <span className="text-primary font-medium">{i + 1}.</span>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Timeline */}
              <div className="flex items-center gap-4 p-3 rounded-md bg-primary/5 border border-primary/20">
                <Clock className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Recommended Timeline</p>
                  <p className="font-medium">{selectedInsight.details.timeline}</p>
                </div>
              </div>

              {/* Related Incidents */}
              {selectedInsight.details.affectedIncidents.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Related Incidents ({selectedInsight.details.affectedIncidents.length})
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedInsight.details.affectedIncidents.slice(0, 5).map((inc) => (
                      <div key={inc.id} className="text-sm p-2 rounded bg-secondary/30 flex items-center justify-between">
                        <span>{inc.title}</span>
                        <Badge variant="outline" className="text-xs">{inc.severity}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resources */}
              {selectedInsight.details.resources.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Available Resources</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedInsight.details.resources.map((resource, i) => (
                      <Badge key={i} variant="outline" className="cursor-pointer hover:bg-secondary">
                        <FileText className="w-3 h-3 mr-1" />
                        {resource}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sub-tabs */}
      <div className="flex items-center gap-2 border-b border-border pb-2">
        <Button 
          variant={activeSubTab === 'recommendations' ? 'secondary' : 'ghost'} 
          size="sm"
          onClick={() => setActiveSubTab('recommendations')}
          className="gap-2"
        >
          <Target className="w-4 h-4" />
          Recommendations
        </Button>
        <Button 
          variant={activeSubTab === 'trends' ? 'secondary' : 'ghost'} 
          size="sm"
          onClick={() => setActiveSubTab('trends')}
          className="gap-2"
        >
          <TrendingUp className="w-4 h-4" />
          Trend Analysis
        </Button>
        <Button 
          variant={activeSubTab === 'patterns' ? 'secondary' : 'ghost'} 
          size="sm"
          onClick={() => setActiveSubTab('patterns')}
          className="gap-2"
        >
          <Clock className="w-4 h-4" />
          Risk Patterns
        </Button>
      </div>

      {/* Action Items */}
      {activeSubTab === 'recommendations' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {actionItems.map((action) => {
            const isCompleted = !!completedTasks[action.id];
            
            return (
              <Card key={action.id} className={`bg-card ${isCompleted ? 'border-green-500/50' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                      isCompleted ? 'bg-green-500/20' : 'bg-secondary'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : action.category === 'Training' ? (
                        <Brain className="w-4 h-4" />
                      ) : action.category === 'Facility' ? (
                        <MapPin className="w-4 h-4" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{action.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        {isCompleted ? (
                          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400 text-xs">
                            Completed
                          </Badge>
                        ) : (
                          <Badge className={`text-xs ${
                            action.priority === 'high' 
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400' 
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400'
                          }`}>
                            {action.priority} priority
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {action.category}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">{action.description}</p>
                  
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-3 mb-4">
                    <p className="text-sm">
                      <span className="font-medium text-amber-700 dark:text-amber-400">Why: </span>
                      <span className="text-amber-900 dark:text-amber-300">{action.reason}</span>
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Timeline</p>
                        <p className="font-medium text-xs">{action.timeline}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Effort</p>
                        <p className="font-medium text-xs">{action.effort}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Impact</p>
                        <p className="font-medium text-xs">{action.impact}</p>
                      </div>
                    </div>
                  </div>

                  {isCompleted ? (
                    <div className="p-3 rounded-md bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-medium">Task Completed</span>
                      </div>
                      <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                        Proof uploaded: {completedTasks[action.id].proof.fileName}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(completedTasks[action.id].completedAt).toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 btn-primary-glow" 
                        size="sm"
                        onClick={() => setSelectedAction(action)}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Proof & Complete
                      </Button>
                      <Button variant="outline" className="flex-1" size="sm">
                        Schedule Review
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload Proof Dialog */}
      <Dialog open={!!selectedAction} onOpenChange={() => { setSelectedAction(null); setUploadFile(null); setUploadNotes(''); }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Upload Proof of Completion
            </DialogTitle>
            <DialogDescription>
              Upload documentation to verify that &ldquo;{selectedAction?.title}&rdquo; has been completed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-3 rounded-md bg-secondary/30">
              <h4 className="font-medium text-sm mb-2">Required Documentation:</h4>
              <ul className="space-y-1">
                {selectedAction?.requirements?.map((req, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                    <Paperclip className="w-3 h-3" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <Label>Upload File</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="bg-secondary/50"
              />
              {uploadFile && (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <Check className="w-4 h-4" />
                  {uploadFile.name}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Accepted formats: PDF, DOC, DOCX, JPG, PNG, Excel
              </p>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Add any additional notes about the completion..."
                rows={3}
                className="bg-secondary/50"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setSelectedAction(null); setUploadFile(null); setUploadNotes(''); }}>
              Cancel
            </Button>
            <Button onClick={handleUploadProof} disabled={!uploadFile} className="btn-primary-glow">
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark as Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {activeSubTab === 'trends' && (
        <Card className="bg-card">
          <CardContent className="p-6">
            <div className="text-center py-12 text-muted-foreground">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Trend Analysis</p>
              <p className="text-sm mt-1">Historical trend data will appear here as more incidents are logged</p>
            </div>
          </CardContent>
        </Card>
      )}

      {activeSubTab === 'patterns' && (
        <Card className="bg-card">
          <CardContent className="p-6">
            <div className="text-center py-12 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Risk Patterns</p>
              <p className="text-sm mt-1">Time-based risk patterns will be analyzed as data accumulates</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Dashboard Stats Component
const DashboardTab = ({ incidents }) => {
  const openCount = incidents.filter(i => i.status === 'open').length;
  const resolvedCount = incidents.filter(i => i.status === 'resolved').length;
  const criticalCount = incidents.filter(i => i.severity === 'critical').length;
  const highCount = incidents.filter(i => i.severity === 'high').length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="metric-card">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Total Incidents</p>
            <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">{incidents.length}</p>
          </CardContent>
        </Card>
        <Card className="metric-card">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Open</p>
            <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1 text-amber-500">{openCount}</p>
          </CardContent>
        </Card>
        <Card className="metric-card">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Resolved</p>
            <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1 text-green-500">{resolvedCount}</p>
          </CardContent>
        </Card>
        <Card className="metric-card">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Critical/High</p>
            <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1 text-red-500">{criticalCount + highCount}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default function Incidents() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [activeTab, setActiveTab] = useState('workflow');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [publicLinks, setPublicLinks] = useState([]);
  const [publicLinksLoading, setPublicLinksLoading] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editForm, setEditForm] = useState({ expires_at: '', max_uses: '' });
  const [savingLink, setSavingLink] = useState(false);
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const { isPlatformMode, isOrganizationMode, selectedOrg } = useAdminMode();

  const [newIncident, setNewIncident] = useState({
    organization_id: '',
    title: '',
    description: '',
    incident_date: new Date().toISOString().split('T')[0],
    category: '',
    severity: 'medium',
    location: '',
  });

  const fetchIncidents = React.useCallback(async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (severityFilter && severityFilter !== 'all') params.severity = severityFilter;
      // Filter by selected org if in organization mode
      if (isOrganizationMode && selectedOrg) {
        params.organization_id = selectedOrg.id;
      }
      const response = await getIncidents(params);
      setIncidents(response.data);
    } catch (error) {
      toast.error('Failed to fetch incidents');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter, isOrganizationMode, selectedOrg]);

  const fetchOrganizations = React.useCallback(async () => {
    try {
      const response = await getOrganizations();
      setOrganizations(response.data);
      if (response.data.length > 0) {
        setNewIncident(prev => ({ ...prev, organization_id: response.data[0].id }));
        setSelectedOrgId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch organizations');
    }
  }, []);

  useEffect(() => {
    // If super admin in platform mode, don't fetch incidents (show portfolio view)
    if (isSuperAdmin() && isPlatformMode) {
      setLoading(false);
      return;
    }
    fetchIncidents();
    fetchOrganizations();
  }, [fetchIncidents, fetchOrganizations, isSuperAdmin, isPlatformMode]);

  // Fetch public links when in organization mode with selected org
  const fetchPublicLinks = React.useCallback(async () => {
    if (!isOrganizationMode || !selectedOrg) return;
    setPublicLinksLoading(true);
    try {
      const response = await getPublicIncidentLinks(selectedOrg.id);
      setPublicLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch public links');
    } finally {
      setPublicLinksLoading(false);
    }
  }, [isOrganizationMode, selectedOrg]);

  useEffect(() => {
    if (isOrganizationMode && selectedOrg) {
      fetchPublicLinks();
    }
  }, [fetchPublicLinks, isOrganizationMode, selectedOrg]);

  const handleGeneratePublicLink = async () => {
    if (!selectedOrg) {
      toast.error('Please select an organization first');
      return;
    }
    setGeneratingLink(true);
    try {
      const response = await createPublicIncidentLink({ 
        organization_id: selectedOrg.id,
        expires_in_days: 30 
      });
      toast.success('Public link generated successfully!');
      setPublicLinks(prev => [response.data, ...prev]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleRevokePublicLink = async (linkId) => {
    try {
      await revokePublicIncidentLink(linkId);
      toast.success('Link revoked successfully');
      setPublicLinks(prev => prev.map(l => l.id === linkId ? { ...l, is_active: false } : l));
    } catch (error) {
      toast.error('Failed to revoke link');
    }
  };

  const handleEditLink = (link) => {
    setEditingLink(link);
    setEditForm({
      expires_at: link.expires_at ? link.expires_at.split('T')[0] : '',
      max_uses: link.max_uses || '',
    });
  };

  const handleSaveLink = async () => {
    if (!editingLink) return;
    setSavingLink(true);
    try {
      const payload = {};
      if (editForm.expires_at) {
        payload.expires_at = new Date(editForm.expires_at + 'T23:59:59Z').toISOString();
      } else {
        payload.expires_at = '';
      }
      payload.max_uses = editForm.max_uses ? parseInt(editForm.max_uses, 10) : 0;

      const response = await updatePublicIncidentLink(editingLink.id, payload);
      toast.success('Link updated successfully');
      setPublicLinks(prev => prev.map(l => l.id === editingLink.id ? response.data : l));
      setEditingLink(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update link');
    } finally {
      setSavingLink(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  // Super Admin Platform Mode - Show Portfolio View
  if (isSuperAdmin() && isPlatformMode) {
    return (
      <div data-testid="incidents-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
              Incident Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Portfolio-level incident analytics across all organizations
            </p>
          </div>
        </div>
        <PortfolioIncidents />
      </div>
    );
  }

  // Super Admin Organization Mode - No org selected
  if (isSuperAdmin() && isOrganizationMode && !selectedOrg) {
    return (
      <div data-testid="incidents-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
              Incident Management
            </h1>
            <p className="text-muted-foreground mt-1">Select an organization to manage incidents</p>
          </div>
        </div>
        
        <Alert className="bg-yellow-500/10 border-yellow-500/30">
          <AlertCircle className="w-4 h-4 text-yellow-600" />
          <AlertDescription className="text-yellow-600">
            Please select an organization from the admin bar above to view and manage incidents.
          </AlertDescription>
        </Alert>
        
        <Card className="p-12 text-center">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Organization Selected</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Use the organization selector in the admin bar to choose an organization and manage its incidents.
          </p>
        </Card>
      </div>
    );
  }

  // Organization context banner for super admins
  const OrgContextBanner = () => {
    if (!isSuperAdmin() || !isOrganizationMode || !selectedOrg) return null;
    return (
      <Alert className="bg-blue-500/10 border-blue-500/30 mb-4">
        <Building2 className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-600">
          Managing incidents for <strong>{selectedOrg.name}</strong>
        </AlertDescription>
      </Alert>
    );
  };

  const handleCreateIncident = async (e) => {
    e.preventDefault();
    try {
      await createIncident(newIncident);
      toast.success('Incident reported successfully');
      setShowCreateDialog(false);
      fetchIncidents();
      setNewIncident({
        organization_id: organizations[0]?.id || '',
        title: '',
        description: '',
        incident_date: new Date().toISOString().split('T')[0],
        category: '',
        severity: 'medium',
        location: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create incident');
    }
  };

  const handleUploadIncidents = async () => {
    if (!uploadFile || !selectedOrgId) {
      toast.error('Please select a file and organization');
      return;
    }

    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('organization_id', selectedOrgId);

    try {
      const response = await uploadIncidents(formData);
      const result = response.data;
      setUploadResult({
        success: true,
        message: result.message,
        count: result.incident_ids?.length || 0
      });
      toast.success(result.message);
      setUploadFile(null);
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      fetchIncidents();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload incidents';
      setUploadResult({
        success: false,
        message: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = `title,description,incident_date,category,severity,location,resolution
Minor Injury - Practice,Athlete twisted ankle during warm-up exercises,2026-01-15,injury_medical,low,Training Field A,Ice applied and rest recommended
Equipment Malfunction,Basketball hoop net was torn during game,2026-01-20,equipment_facility,medium,Main Gymnasium,Net replaced the following day
Misconduct Report,Verbal altercation between participants,2026-01-22,abuse_misconduct,medium,Lobby Area,Meeting held with involved parties`;
    
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'incident_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Sample template downloaded');
  };

  const handleAnalyze = async (incidentId) => {
    setAnalyzing(incidentId);
    try {
      await analyzeIncident(incidentId);
      toast.success('AI analysis complete');
      fetchIncidents();
    } catch (error) {
      toast.error('Failed to analyze incident');
    } finally {
      setAnalyzing(null);
    }
  };

  const filteredIncidents = incidents.filter(incident => {
    const matchesSearch = incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      incident.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || incident.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getSeverityClass = (severity) => {
    const classes = {
      critical: 'severity-critical',
      high: 'severity-high',
      medium: 'severity-medium',
      low: 'severity-low',
    };
    return classes[severity] || '';
  };

  const getStatusClass = (status) => {
    const classes = {
      open: 'status-open',
      'in-progress': 'status-in-progress',
      resolved: 'status-resolved',
      imported: 'status-imported',
    };
    return classes[status] || '';
  };

  return (
    <div data-testid="incidents-page" className="space-y-6 animate-fade-in">
      {/* Org Context Banner */}
      <OrgContextBanner />
      

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            {selectedOrg ? `${selectedOrg.name} - Incidents` : 'Incident Management'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitor, report, and manage incidents across your organization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => exportIncidentsReport(incidents, selectedOrg?.name || 'All Organizations')}
            disabled={incidents.length === 0}
            data-testid="export-incidents-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-incident-btn" className="btn-primary-glow">
                <Plus className="w-4 h-4 mr-2" />
                Report Incident
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight text-xl">
                Report New Incident
              </DialogTitle>
              <DialogDescription>
                Complete the form below to report an incident. Required fields are marked with *.
              </DialogDescription>
            </DialogHeader>
            <IncidentReportForm
              formType={selectedOrg?.org_type === 'fitness' ? 'fitness' : 'sports'}
              organizationId={selectedOrg?.id || user?.organization_id}
              onSuccess={() => {
                setShowCreateDialog(false);
                fetchIncidents();
              }}
              onCancel={() => setShowCreateDialog(false)}
            />
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-secondary/30 p-1 h-auto flex-wrap">
          <TabsTrigger value="workflow" className="gap-2 data-[state=active]:bg-background">
            <LayoutGrid className="w-4 h-4" />
            Workflow
          </TabsTrigger>
          <TabsTrigger value="dashboard" className="gap-2 data-[state=active]:bg-background">
            <LayoutDashboard className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="list" className="gap-2 data-[state=active]:bg-background">
            <List className="w-4 h-4" />
            All Incidents
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-2 data-[state=active]:bg-background">
            <Lightbulb className="w-4 h-4" />
            AI Insights
          </TabsTrigger>
          <TabsTrigger value="import" className="gap-2 data-[state=active]:bg-background">
            <FileUp className="w-4 h-4" />
            Import Data
          </TabsTrigger>
          <TabsTrigger value="share" className="gap-2 data-[state=active]:bg-background">
            <LinkIcon className="w-4 h-4" />
            Public Link
          </TabsTrigger>
        </TabsList>

        {/* Workflow Tab */}
        <TabsContent value="workflow" className="mt-6">
          <IncidentWorkflowDashboard />
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-6">
          <DashboardTab incidents={incidents} />
        </TabsContent>

        {/* All Incidents Tab */}
        <TabsContent value="list" className="mt-6">
          {/* Filters */}
          <Card className="bg-card border-border mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search incidents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-secondary/50"
                    data-testid="search-incidents-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {STATUS_OPTIONS.map(status => (
                      <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={severityFilter} onValueChange={setSeverityFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severity</SelectItem>
                    {SEVERITY_OPTIONS.map(sev => (
                      <SelectItem key={sev} value={sev} className="capitalize">{sev}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {CATEGORY_OPTIONS.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Incidents Table */}
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="spinner" />
                </div>
              ) : filteredIncidents.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-border">
                      <TableHead>Incident</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>AI Analysis</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredIncidents.map((incident) => {
                      const categoryKey = incident.primary_category || incident.category?.toLowerCase().replace(/[^a-z]/g, '_');
                      const CategoryIcon = CATEGORY_ICONS[categoryKey] || AlertTriangle;
                      return (
                        <TableRow 
                          key={incident.id} 
                          className="table-row-hover cursor-pointer border-border"
                          onClick={() => navigate(`/incidents/${incident.id}`)}
                          data-testid={`incident-row-${incident.id}`}
                        >
                          <TableCell>
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                incident.primary_category === 'abuse_misconduct' ? 'bg-red-500/10' :
                                incident.primary_category === 'injury_medical' ? 'bg-orange-500/10' :
                                'bg-secondary/50'
                              }`}>
                                <CategoryIcon className={`w-4 h-4 ${
                                  incident.primary_category === 'abuse_misconduct' ? 'text-red-500' :
                                  incident.primary_category === 'injury_medical' ? 'text-orange-500' :
                                  'text-muted-foreground'
                                }`} />
                              </div>
                              <div>
                                <p className="font-medium">{incident.title}</p>
                                <p className="text-xs text-muted-foreground line-clamp-1">
                                  {incident.description}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {incident.secondary_type || incident.category}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${getSeverityClass(incident.severity)} text-xs uppercase`}>
                              {incident.severity}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${getStatusClass(incident.status)} text-xs uppercase`}>
                              {incident.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(incident.incident_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {incident.ai_analysis ? (
                              <Badge variant="outline" className="text-xs text-primary border-primary">
                                Analyzed
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">Pending</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAnalyze(incident.id);
                              }}
                              disabled={analyzing === incident.id}
                              data-testid={`analyze-btn-${incident.id}`}
                            >
                              {analyzing === incident.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Brain className="w-4 h-4" />
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <AlertTriangle className="w-12 h-12 mb-4 opacity-50" />
                  <p>No incidents found</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowCreateDialog(true)}
                  >
                    Report First Incident
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights" className="mt-6">
          <AIInsightsTab incidents={incidents} organizations={organizations} />
        </TabsContent>

        {/* Import Data Tab */}
        <TabsContent value="import" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Upload Form */}
            <Card className="bg-card border-border">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                  <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                  Import Historical Data
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Upload incident records from CSV, Excel, or PDF files
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
                {/* Organization Selection */}
                <div className="space-y-2">
                  <Label className="text-sm">Organization</Label>
                  <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                    <SelectTrigger className="w-full" data-testid="import-org-selector">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="truncate">{org.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label className="text-sm">Upload File</Label>
                  <div className="border-2 border-dashed border-border rounded-lg p-4 sm:p-6 text-center hover:border-primary/50 transition-colors">
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls,.pdf"
                      onChange={(e) => {
                        setUploadFile(e.target.files[0]);
                        setUploadResult(null);
                      }}
                      className="hidden"
                      id="incident-file-upload"
                      data-testid="import-file-input"
                    />
                    <label htmlFor="incident-file-upload" className="cursor-pointer">
                      <FileUp className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 text-muted-foreground" />
                      {uploadFile ? (
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-primary">{uploadFile.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(uploadFile.size / 1024).toFixed(1)} KB
                          </p>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => {
                              e.preventDefault();
                              setUploadFile(null);
                              setUploadResult(null);
                              const input = document.getElementById('incident-file-upload');
                              if (input) input.value = '';
                            }}
                            className="text-xs text-red-500 hover:text-red-600"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm font-medium">Click to upload or drag and drop</p>
                          <p className="text-xs text-muted-foreground mt-1">CSV, Excel (.xlsx, .xls), or PDF</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                {/* Upload Result */}
                {uploadResult && (
                  <Alert className={uploadResult.success ? 'border-green-500/50 bg-green-500/10' : 'border-red-500/50 bg-red-500/10'}>
                    {uploadResult.success ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                    <AlertDescription className="text-sm">
                      {uploadResult.message}
                      {uploadResult.count > 0 && (
                        <span className="block mt-1 text-xs text-muted-foreground">
                          View imported incidents in the "All Incidents" tab
                        </span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Upload Button */}
                <Button 
                  onClick={handleUploadIncidents} 
                  className="w-full btn-primary-glow" 
                  disabled={!uploadFile || !selectedOrgId || uploading}
                  data-testid="upload-incidents-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Incidents
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Instructions & Template */}
            <Card className="bg-card border-border">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                  <Info className="w-4 h-4 sm:w-5 sm:h-5" />
                  File Format Guide
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
                {/* Required Columns */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                    Required Columns
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 rounded bg-secondary/30">
                      <code className="text-primary">title</code>
                      <p className="text-muted-foreground mt-0.5">Incident title</p>
                    </div>
                    <div className="p-2 rounded bg-secondary/30">
                      <code className="text-primary">description</code>
                      <p className="text-muted-foreground mt-0.5">Full description</p>
                    </div>
                    <div className="p-2 rounded bg-secondary/30">
                      <code className="text-primary">incident_date</code>
                      <p className="text-muted-foreground mt-0.5">YYYY-MM-DD</p>
                    </div>
                    <div className="p-2 rounded bg-secondary/30">
                      <code className="text-primary">category</code>
                      <p className="text-muted-foreground mt-0.5">See categories below</p>
                    </div>
                    <div className="p-2 rounded bg-secondary/30 col-span-2">
                      <code className="text-primary">severity</code>
                      <p className="text-muted-foreground mt-0.5">low, medium, high, or critical</p>
                    </div>
                  </div>
                </div>

                {/* Optional Columns */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Optional Columns</h4>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <Badge variant="outline">location</Badge>
                    <Badge variant="outline">resolution</Badge>
                  </div>
                </div>

                {/* Valid Categories */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Valid Categories</h4>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <Badge className="bg-red-500/20 text-red-500">injury_medical</Badge>
                    <Badge className="bg-orange-500/20 text-orange-500">abuse_misconduct</Badge>
                    <Badge className="bg-yellow-500/20 text-yellow-500">equipment_facility</Badge>
                    <Badge className="bg-blue-500/20 text-blue-500">weather_environmental</Badge>
                    <Badge className="bg-purple-500/20 text-purple-500">policy_violation</Badge>
                    <Badge className="bg-gray-500/20 text-gray-500">other</Badge>
                  </div>
                </div>

                {/* Download Template */}
                <div className="pt-2 border-t border-border">
                  <Button 
                    variant="outline" 
                    onClick={downloadSampleCSV}
                    className="w-full"
                    data-testid="download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Sample Template
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Download a pre-formatted CSV template with example data
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Public Link Tab */}
        <TabsContent value="share" className="mt-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <LinkIcon className="w-5 h-5" />
                    Public Incident Reporting Links
                  </CardTitle>
                  <CardDescription>
                    Generate shareable links for anonymous incident reporting
                  </CardDescription>
                </div>
                <Button 
                  onClick={handleGeneratePublicLink}
                  disabled={generatingLink}
                  data-testid="generate-public-link-btn"
                >
                  {generatingLink ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Generate New Link
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {publicLinksLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : publicLinks.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <LinkIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No public links created yet</p>
                  <p className="text-sm mt-1">Generate a link to allow anonymous incident reporting</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {publicLinks.map((link) => (
                    <div 
                      key={link.id} 
                      className={`p-4 rounded-lg border ${link.is_active ? 'bg-card' : 'bg-muted opacity-60'}`}
                      data-testid={`public-link-${link.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant={link.is_active ? 'default' : 'secondary'}>
                              {link.is_active ? 'Active' : 'Revoked'}
                            </Badge>
                            {link.expires_at && (
                              <span className="text-sm text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Expires: {new Date(link.expires_at).toLocaleDateString()}
                              </span>
                            )}
                            {!link.expires_at && link.is_active && (
                              <span className="text-sm text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                No expiration
                              </span>
                            )}
                            <span className="text-sm text-muted-foreground">
                              Used: {link.uses_count} {link.max_uses ? `/ ${link.max_uses}` : 'times'}
                            </span>
                          </div>
                          <code className="text-sm bg-muted px-3 py-2 rounded block truncate">
                            {link.link_url}
                          </code>
                          <p className="text-xs text-muted-foreground mt-2">
                            Created by {link.created_by_name || 'Unknown'} on {new Date(link.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {link.is_active && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyToClipboard(link.link_url)}
                                data-testid={`copy-link-${link.id}`}
                              >
                                Copy Link
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditLink(link)}
                                data-testid={`edit-link-${link.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={() => handleRevokePublicLink(link.id)}
                                data-testid={`revoke-link-${link.id}`}
                              >
                                Revoke
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Link Dialog */}
          <Dialog open={!!editingLink} onOpenChange={(open) => { if (!open) setEditingLink(null); }}>
            <DialogContent className="bg-card border-border max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Pencil className="w-5 h-5" />
                  Edit Public Link
                </DialogTitle>
                <DialogDescription>
                  Update the expiration date or usage limits for this link.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label>Expiration Date</Label>
                  <Input
                    type="date"
                    value={editForm.expires_at}
                    onChange={(e) => setEditForm(prev => ({ ...prev, expires_at: e.target.value }))}
                    className="bg-secondary/50"
                    data-testid="edit-link-expires-input"
                  />
                  <p className="text-xs text-muted-foreground">Leave empty for no expiration</p>
                </div>
                <div className="space-y-2">
                  <Label>Max Uses</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Unlimited"
                    value={editForm.max_uses}
                    onChange={(e) => setEditForm(prev => ({ ...prev, max_uses: e.target.value }))}
                    className="bg-secondary/50"
                    data-testid="edit-link-max-uses-input"
                  />
                  <p className="text-xs text-muted-foreground">Set to 0 or leave empty for unlimited</p>
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setEditingLink(null)}>Cancel</Button>
                <Button onClick={handleSaveLink} disabled={savingLink} data-testid="save-link-btn">
                  {savingLink ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
